import {
  HeaderProject,
  DnDNodeProps,
  selectUser,
  HeaderProjectPreview,
  AvatarProps,
} from '@centerpiece/components';
import moment from 'moment';
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCreateProjectTemplateMutation,
  usePatchProjectMutation,
  useLazyGetProjectContactsQuery,
  useCreateProjectContactMutation,
  useDeleteProjectContactMutation,
  subscribeProjectContacts,
} from 'store/api';
import { useLogout } from 'utils/logout.utils';
import { waitForElm } from 'utils/project.utils';
import { useSelector } from 'react-redux';
import { getItemStatus } from 'utils/status.utils';
import { useProjectContext } from 'contexts/project.context';
import {
  Comment,
  Subscriber,
  Stage as StageModel,
  Grouping as GroupingModel,
  ProjectContact,
  Project,
} from 'store/api/models';
import { toast } from 'react-toastify';
import { projectSteps } from 'tutorials/project.tutorial';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { ContactTooltip } from '../ContactTooltip';

export interface ProjectHeaderProps {
  onBack?: Function;
  onLogoClick?: Function;
  setOpenProjectInfo: React.Dispatch<React.SetStateAction<string | undefined>>;
  setOpenSpeedDial: React.Dispatch<React.SetStateAction<boolean>>;
  append?: any;
}

export const ProjectHeader = ({
  onBack,
  onLogoClick,
  setOpenProjectInfo,
  setOpenSpeedDial,
  append,
}: ProjectHeaderProps) => {
  const {
    id,
    project,
    projectLoading,
    projectColors,
    preview,
    template,
    disabled,
    items,
    itemsFetching,
    comments,
    tags,
    project_contacts,
    msgs,
    cmts,
    projectTree,
    events,
    project_stages,
    project_states,
    refetchProject,
    currentStage,
    nextStage,
    statuses,
    subscribers,
    completeChildren,
    patchProject,
    setAlertDetails,
    setActiveNode,
  } = useProjectContext();
  const navigate = useNavigate();
  const onLogout = useLogout();
  const [loading, setLoading] = useState<boolean>(false);
  const itemsRef = useRef({});
  const [filter, setFilter] = useState<string>('Active');
  const [itemType, setItemType] = useState<{
    label: string;
    filter: string;
  }>({
    label: 'All',
    filter: 'All',
  });
  const [selectedTag, setSelectedTag] = useState<{
    name: string;
    id: number;
  }>({
    name: 'All',
    id: -1,
  });

  // eslint-disable-next-line
  const { contact: userContact } = useSelector(selectUser);

  // const [patchProject] = usePatchProjectMutation();
  const [createProjectTemplate] = useCreateProjectTemplateMutation();

  const { state, setState } = useTutorialContext();

  const numChats = comments?.filter(
    (comment: Comment) =>
      comment?.contacts?.filter(
        (contact: any) =>
          contact.status === 'New' && contact.contact === userContact
      )?.length > 0
  )?.length;
  return (
    <>
      {preview ? (
        <HeaderProjectPreview
          title={`${template ? `Template` : ``} ${preview ? `Preview` : ``}: ${
            project?.title || ''
          }`}
          onBack={onBack}
        />
      ) : (
        <HeaderProject
          append={append}
          title={
            template
              ? `Template: ${project?.title || ''}`
              : project?.archived
              ? `Archived: ${project?.title || ''}`
              : `${project?.title || ''}`
          }
          save={
            !project?.archived
              ? (newtitle: string) =>
                  patchProject({
                    id: Number(id),
                    title: newtitle,
                  } as any)
              : undefined
          }
          assignees={{
            force: true,
            data: project_contacts.map(
              (pc: ProjectContact) =>
                ({
                  contact_r: { ...pc.contact_r },
                  group: 'subscriber',
                } as AvatarProps)
            ),
            disabled: true,
            tooltip: ContactTooltip,
            // options: subscribers?.map((subscriber: Subscriber) => ({
            //   ...subscriber?.contact_r,
            //   contact: subscriber?.contact_r?.id,
            //   name:
            //     subscriber?.contact_r.firstname &&
            //     subscriber?.contact_r.firstname !== ''
            //       ? `${subscriber.contact_r.firstname} ${subscriber.contact_r.lastname}`
            //       : subscriber.contact_r.email,
            // })),
            // lazyQuery: useLazyGetProjectContactsQuery,
            // queryParams: {
            //   params: {
            //     project: id,
            //     nested: 'all',
            //   },
            // },
            // subscription: subscribeProjectContacts,
            // createMutation: useCreateProjectContactMutation,
            // deleteMutation: useDeleteProjectContactMutation,
            // onCreate: (item: any) => ({
            //   contact: item.contact,
            //   project: id,
            // }),
            reloadParent: () => refetchProject(),
          }}
          caption={template || !project?.due_date ? '' : 'Project due'}
          caption_details={
            template || !project?.due_date
              ? ''
              : moment(project?.due_date).format('MM/DD/YYYY')
          }
          status={getItemStatus({
            status: project?.state_r || ({} as any),
            disabled: disabled || template,
            statuses: project_states,
            color: projectColors?.status?.border,
            onClick: (state: any) => {
              patchProject({
                id: Number(id),
                state: state.id,
              } as any);
            },
          })}
          onLogoClick={onLogoClick ? () => onLogoClick() : () => navigate('/')}
          onDashboardClick={() => {
            setOpenProjectInfo('info');
          }}
          onFolderClick={
            !template
              ? () => {
                  setOpenProjectInfo('files');
                }
              : undefined
          }
          // menuBadge={numChats}
          menuItems={[
            // {
            //   badge: numChats,
            //   label: 'Project Details',
            //   onClick: () => {
            //     setOpenProjectInfo('info');
            //   },
            // },
            {
              label: `${!project?.archived ? 'Archive' : 'Unarchive'} Project`,
              onClick: async () =>
                setAlertDetails({
                  open: true,
                  title: `${
                    project?.archived ? 'Unarchive' : 'Archive'
                  } Project?`,
                  content: `Are you sure you want to ${
                    project?.archived ? 'unarchive' : 'archive'
                  } this project?`,
                  onConfirm: async () => {
                    await patchProject({
                      id: id,
                      archived: !project?.archived,
                    } as Project);
                  },
                }),
            },
            ...(template
              ? []
              : [
                  {
                    label: 'Create Template from Project',
                    onClick: async () => {
                      try {
                        const newTemplate = await createProjectTemplate({
                          title: project?.title,
                          details: project?.details,
                          project: Number(id),
                        }).unwrap();
                        navigate(`/project_template/${newTemplate?.project}`);
                        toast.info('Template created successfuly');
                      } catch (error) {
                        toast.error('Unable to create project template');
                      }
                    },
                  },
                ]),
            {
              label: `Show ${filter === 'Active' ? 'Archived' : 'Active'}`,
              onClick: () =>
                setFilter(filter === 'Active' ? 'Archived' : 'Active'),
            },
            {
              label: 'Tutorial',
              onClick: () =>
                setState(currState => {
                  return {
                    ...currState,
                    steps: projectSteps(),
                    stepIndex: 0,
                    run: true,
                    triggers: {
                      ...currState.triggers,
                      openProjectDetailsDrawer: () =>
                        setOpenProjectInfo('info'),
                      closeProjectDetailsDrawer: () =>
                        setOpenProjectInfo(undefined),
                      toggleCalendar: () => {
                        document.getElementById('calendarExpand')?.click();
                      },
                      openCoordinationDrawer: async () => {
                        const elemID = `coordination-21198`;
                        const currentItem = (itemsRef.current as any)[elemID];
                        await currentItem.expandParent();
                        const currentNode = (itemsRef.current as any)[
                          `item-21198`
                        ];
                        currentNode.onClick();
                      },
                      closeCoordinationDrawer: () => setActiveNode(undefined),
                      openTaskDrawer: async () => {
                        const elemID = `task-21197`;
                        const currentItem = (itemsRef.current as any)[elemID];
                        await currentItem.expandParent();
                        const currentNode = (itemsRef.current as any)[
                          `item-21197`
                        ];
                        currentNode.onClick();
                      },
                      closeTaskDrawer: () => setActiveNode(undefined),
                      openSpeedDial: () => setOpenSpeedDial(true),
                      closeSpeedDial: () => setOpenSpeedDial(false),
                    },
                  };
                }),
            },
            { label: 'Logout', onClick: () => onLogout() },
          ]}
        />
      )}
    </>
  );
};
